<template>
  <v-app-bar
    app
    :color="isDark ? 'surface' : 'white'"
    elevation="1"
    height="64"
    class="derracare-navbar"
  >
    <div class="d-flex align-center w-100 px-6">

      <!-- Back Button (Desktop & Mobile) -->
      <v-btn
        v-if="showBackButton"
        icon="mdi-arrow-left"
        variant="text"
        color="grey-darken-3"
        class="mr-2"
        @click="goBack"
        title="Go Back"
      />

      <!-- DerraCare Logo -->
      <router-link
        :to="adminStore.isAuthenticated ? '/admin/dashboard' : '/dashboard'"
        class="text-decoration-none"
      >
        <h2 class="derracare-title gradient-text">DerraCare</h2>
      </router-link>

      <v-spacer />

      <!-- Navigation Menu -->
      <div class="d-flex align-center">
        <!-- Theme Toggle -->
        <v-btn icon @click="toggleTheme" class="mr-2" size="small">
          <v-icon>{{
            isDark ? "mdi-weather-sunny" : "mdi-weather-night"
          }}</v-icon>
        </v-btn>

        <!-- WhatsApp Status Indicator -->
        <v-btn
          v-if="!adminStore.isAuthenticated"
          icon
          @click="openWhatsAppSetup"
          class="mr-3"
          size="small"
        >
          <v-badge
            v-if="!isWhatsAppVerified"
            color="warning"
            dot
            offset-x="10"
            offset-y="10"
          >
            <v-icon color="grey-darken-1">mdi-whatsapp</v-icon>
          </v-badge>
          <v-icon v-else color="success">mdi-whatsapp</v-icon>
        </v-btn>

        <!-- User Avatar -->
        <v-menu offset-y>
          <template #activator="{ props }">
            <v-avatar
              size="40"
              class="cursor-pointer"
              v-bind="props"
              :color="getAvatarColor(userDisplayName)"
            >
              <v-img
                v-if="userAvatarUrl"
                :src="userAvatarUrl"
                :alt="userDisplayName"
              />
              <span v-else class="text-white text-body-2 font-weight-bold">{{
                getInitials(userDisplayName)
              }}</span>
            </v-avatar>
          </template>
          <v-card min-width="200">
            <v-list class="pa-0">
              <v-list-item class="py-3 px-4">
                <template #prepend>
                  <v-avatar
                    size="40"
                    class="mr-3"
                    :color="getAvatarColor(userDisplayName)"
                  >
                    <v-img
                      v-if="userAvatarUrl"
                      :src="userAvatarUrl"
                      :alt="userDisplayName"
                    />
                    <span
                      v-else
                      class="text-white text-body-2 font-weight-bold"
                      >{{ getInitials(userDisplayName) }}</span
                    >
                  </v-avatar>
                </template>
                <v-list-item-title class="font-weight-medium">
                  {{ displayName }}
                </v-list-item-title>
                <v-list-item-subtitle>
                  {{ displayEmail }}
                </v-list-item-subtitle>
              </v-list-item>
              <v-divider />
              <v-list-item to="/settings" class="py-2">
                <template #prepend>
                  <v-icon>mdi-cog</v-icon>
                </template>
                <v-list-item-title>Settings</v-list-item-title>
              </v-list-item>
              <v-divider />
              <v-list-item @click="logout" class="py-2 logout-item">
                <template #prepend>
                  <v-icon color="#dc2626">mdi-logout</v-icon>
                </template>
                <v-list-item-title class="logout-text"
                  >Logout</v-list-item-title
                >
              </v-list-item>
            </v-list>
          </v-card>
        </v-menu>
      </div>
    </div>
  </v-app-bar>

  <!-- WhatsApp Setup Dialog -->
  <WhatsAppSetupDialog
    v-model="showWhatsAppDialog"
    @close="showWhatsAppDialog = false"
  />
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from "vue";
import { useRouter } from "vue-router";
import { useAuthStore } from "@/stores/auth";
import { useAdminStore } from "@/stores/admin";
import { useAppStore } from "@/stores/app";
import { formatDistanceToNow } from "date-fns";
import WhatsAppSetupDialog from "@/components/whatsapp/WhatsAppSetupDialog.vue";
import { getInitials, getAvatarColor, resolveAvatarUrl } from "@/utils/avatar";

import { useTheme } from "vuetify";

const router = useRouter();
const theme = useTheme();
const authStore = useAuthStore();
const adminStore = useAdminStore();
const appStore = useAppStore();

// Navigation Logic
const showBackButton = computed(() => {
  const rootRoutes = ["Dashboard", "AdminDashboard", "Login", "AdminLogin"];
  return route.name && !rootRoutes.includes(route.name as string);
});

const goBack = () => {
  router.back();
};

// Theme Logic
const isDark = ref(false);

const toggleTheme = () => {
  isDark.value = !isDark.value;
  const newTheme = isDark.value ? "dark" : "light";

  // Update Vuetify Theme
  theme.global.name.value = newTheme;

  // Update Tailwind/Global Class
  if (isDark.value) {
    document.documentElement.classList.add("dark");
    localStorage.setItem("theme", "dark");
  } else {
    document.documentElement.classList.remove("dark");
    localStorage.setItem("theme", "light");
  }
};

onMounted(() => {
  // Check local storage or system preference
  const storedTheme = localStorage.getItem("theme");
  const prefersDark = window.matchMedia("(prefers-color-scheme: dark)").matches;

  if (storedTheme === "dark" || (!storedTheme && prefersDark)) {
    isDark.value = true;
    theme.global.name.value = "dark";
    document.documentElement.classList.add("dark");
  } else {
    isDark.value = false;
    theme.global.name.value = "light";
    document.documentElement.classList.remove("dark");
  }
});

const notifications = ref([
  {
    id: 1,
    title: "New Appointment",
    message: "John Smith scheduled an appointment for tomorrow",
    type: "appointment",
    read: false,
    createdAt: new Date(Date.now() - 10 * 60 * 1000), // 10 minutes ago
  },
  {
    id: 2,
    title: "Patient Message",
    message: "Sarah Johnson sent you a message",
    type: "message",
    read: false,
    createdAt: new Date(Date.now() - 2 * 60 * 60 * 1000), // 2 hours ago
  },
]);

// Resolved name for avatar — used for both color and initials
const userDisplayName = computed(() => {
  if (adminStore.isAuthenticated) {
    return adminStore.admin?.full_name || "Admin";
  }
  return authStore.doctor?.name || "Doctor";
});

// Resolved avatar URL — only trusted sources (Cloudinary / own server); null → show initials
const userAvatarUrl = computed(() => {
  const raw = adminStore.isAuthenticated
    ? adminStore.admin?.profile_image_url
    : authStore.doctor?.profile_image_url;
  return resolveAvatarUrl(raw);
});

const displayName = computed(() => {
  if (adminStore.isAuthenticated) {
    return adminStore.admin?.full_name || "Admin";
  }
  return authStore.doctor?.name || "Doctor";
});

const displayEmail = computed(() => {
  if (adminStore.isAuthenticated) {
    return adminStore.admin?.email;
  }
  return authStore.doctor?.email;
});

const unreadNotifications = computed(() => {
  return notifications.value.filter((n) => !n.read).length;
});

const getRelativeTime = (date: Date) => {
  return formatDistanceToNow(date, { addSuffix: true });
};

const markAllAsRead = () => {
  notifications.value.forEach((n) => (n.read = true));
};

// WhatsApp Setup Dialog
const showWhatsAppDialog = ref(false);

const isWhatsAppVerified = computed(() => {
  return authStore.doctor?.whatsapp_verified || false;
});

const openWhatsAppSetup = () => {
  showWhatsAppDialog.value = true;
};

const logout = async () => {
  if (adminStore.isAuthenticated) {
    await adminStore.logout();
    router.push("/admin/login");
  } else {
    await authStore.logout();
    router.push("/login");
  }
};

onMounted(() => {
  // Load notifications
});
</script>

<style scoped lang="scss">
// DerraCare NavBar Design
.derracare-navbar {
  border-bottom: 1px solid rgba(0, 0, 0, 0.05) !important;
  background: rgba(255, 255, 255, 0.7) !important;
  backdrop-filter: blur(20px);
  -webkit-backdrop-filter: blur(20px);
}

.gradient-text {
  background: linear-gradient(135deg, #111618 0%, #1193d4 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

.derracare-title {
  font-size: 20px;
  font-weight: 700;
  color: #111618;
  margin: 0;
  font-family: "Inter", "Noto Sans", sans-serif;
}

.nav-link {
  font-size: 14px;
  font-weight: 500;
  text-transform: none;
  letter-spacing: normal;

  &--active {
    color: #6366f1 !important;
    font-weight: 600;
  }
}

.cursor-pointer {
  cursor: pointer;
}

// Logout styling
.logout-item {
  &:hover {
    background-color: #fef2f2 !important;
  }
}

.logout-text {
  color: #dc2626 !important;
  font-weight: 500;
}

// Responsive design
@media (max-width: 768px) {
  .nav-link {
    display: none;
  }

  .healthplus-title {
    font-size: 16px;
  }
}

// Global button text transform override
:deep(.v-btn) {
  text-transform: none !important;
}

:global(html.dark) .derracare-navbar {
  background-color: #111317 !important;
  border-bottom: 1px solid rgba(255, 255, 255, 0.08) !important;
}

:global(html.dark) .derracare-title {
  color: #f3f4f6 !important;
}
</style>
